import { create } from 'zustand';

/**
 * Sync Store - Tracks synchronization status across all stores (GRO-54)
 *
 * States:
 * - synced: All data saved to cloud
 * - syncing: Currently saving
 * - offline: No network, changes queued
 * - error: Sync failed
 */
export const useSyncStore = create((set, get) => ({
    // Sync status
    status: 'synced', // 'synced' | 'syncing' | 'offline' | 'error'

    // Pending sync operations count
    pendingCount: 0,

    // Last successful sync timestamp
    lastSyncedAt: null,

    // Error message if any
    errorMessage: null,

    // Network status
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,

    // ===== ACTIONS =====

    /**
     * Mark sync as starting
     */
    startSync: () => {
        set((state) => ({
            status: state.isOnline ? 'syncing' : 'offline',
            pendingCount: state.pendingCount + 1,
            errorMessage: null,
        }));
    },

    /**
     * Mark sync as completed successfully
     */
    completeSync: () => {
        set((state) => {
            const newPendingCount = Math.max(0, state.pendingCount - 1);
            return {
                pendingCount: newPendingCount,
                status: newPendingCount === 0 ? 'synced' : 'syncing',
                lastSyncedAt: newPendingCount === 0 ? new Date().toISOString() : state.lastSyncedAt,
                errorMessage: null,
            };
        });
    },

    /**
     * Mark sync as failed
     * @param {string} message - Error message
     */
    failSync: (message = 'Sync failed') => {
        set((state) => ({
            status: 'error',
            pendingCount: Math.max(0, state.pendingCount - 1),
            errorMessage: message,
        }));
    },

    /**
     * Set online/offline status
     * @param {boolean} isOnline
     */
    setOnlineStatus: (isOnline) => {
        set((state) => ({
            isOnline,
            status: !isOnline ? 'offline' : (state.pendingCount > 0 ? 'syncing' : 'synced'),
        }));
    },

    /**
     * Clear error and reset to synced
     */
    clearError: () => {
        set({
            status: 'synced',
            errorMessage: null,
        });
    },

    /**
     * Get formatted last sync time
     */
    getLastSyncedFormatted: () => {
        const { lastSyncedAt } = get();
        if (!lastSyncedAt) return 'Never';

        const date = new Date(lastSyncedAt);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;

        return date.toLocaleDateString();
    },
}));

// Set up online/offline listeners
if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
        useSyncStore.getState().setOnlineStatus(true);
    });

    window.addEventListener('offline', () => {
        useSyncStore.getState().setOnlineStatus(false);
    });
}
